/**
 * Base Item Class
 *
 * Represents any item in the game (weapons, potions, consumables, etc.)
 * Uses composition pattern for flexible item behavior
 */

import { getSharedLogger } from './utils/loggerProxy.js';
import { resolveSound } from './resolveSound.js';

const itemLogger = getSharedLogger('ITEM');

// Item types for categorization. These double as the item *kinds* an
// inventory slot can hold (shared/ItemRef.js), so an entry here is a promise
// that something can actually be one. ARMOR and MATERIAL were listed for years
// with no item, no system and no config ever using them.
const ItemType = {
  WEAPON: 'weapon',
  CONSUMABLE: 'consumable',
  AMMO: 'ammo',
  COSMETIC: 'cosmetic'
};

// Item rarity tiers
const ItemRarity = {
  COMMON: 'common',
  UNCOMMON: 'uncommon',
  RARE: 'rare',
  EPIC: 'epic',
  LEGENDARY: 'legendary'
};

class Item {
  constructor(config) {
    // Core properties
    this.id = config.id;                           // Unique item identifier (e.g., 'laser_rifle')
    this.name = config.name;                       // Display name (e.g., 'Laser Rifle')
    this.description = config.description || '';   // Item description
    this.type = config.type;                       // ItemType enum
    this.rarity = config.rarity || ItemRarity.COMMON;

    // Visual properties
    this.sprite = config.sprite || null;           // Path to item sprite
    this.icon = config.icon || null;               // Path to inventory icon
    this.dimensions = config.dimensions || { width: 32, height: 32 };

    // Gameplay properties
    this.isEquippable = config.isEquippable || false;
    this.isStackable = config.isStackable || false;
    this.maxStackSize = config.maxStackSize || 1;
    this.canDrop = config.canDrop !== false;       // Can be dropped as loot (default true)

    // Loot properties
    this.dropWeight = config.dropWeight || 1;      // Weight for loot tables
    this.lootSources = config.lootSources || [];   // Where this can be found ['chest', 'ground', 'enemy']

    // Sound effects (event -> path string or { path, volume })
    this.sounds = config.sounds || {};
    // Example: { use: 'assets/sound/potion_drink.wav', equip: { path: 'assets/sound/equip.wav', volume: 0.5 } }

    // Custom metadata (for game-specific data)
    this.metadata = config.metadata || {};
  }

  /**
   * Get sound path for a specific event
   * @param {string} event - Event name (e.g., 'use', 'equip', 'fire')
   * @returns {string|null} - Path to sound file or null
   */
  getSound(event) {
    return resolveSound(this.sounds[event])?.path || null;
  }

  /**
   * Get playback-ready sound for a specific event
   * @param {string} event - Event name (e.g., 'use', 'equip', 'fire')
   * @returns {{ path: string, volume: number }|null}
   */
  getSoundPlayback(event) {
    return resolveSound(this.sounds[event]);
  }

  /**
   * Check if item can be used
   * Override in subclasses for specific logic
   */
  canUse(context) {
    return true;
  }

  /**
   * Use the item
   * Override in subclasses for specific behavior
   */
  use(context) {
    itemLogger.info(`Using item: ${this.name}`);
  }

  /**
   * Serialize item for network transmission
   */
  serialize() {
    return {
      id: this.id,
      name: this.name,
      type: this.type,
      rarity: this.rarity,
      metadata: this.metadata
    };
  }
}

export { Item, ItemType, ItemRarity };
